import { Music, Upload } from "lucide-react";
import React, { useState } from "react";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

interface CreateAlbumDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; artist: string; description?: string; coverUrl?: string }) => void;
}

export const CreateAlbumDialog: React.FC<CreateAlbumDialogProps> = ({ isOpen, onClose, onSubmit }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string>("");
  const { addAlbum } = useLocalLibraryStore();

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      artist: "", // Add artist state if needed
      description,
      coverUrl: coverPreview,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <DialogTitle>Create Album</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex justify-center mb-6">
            <div className="relative w-32 h-32">
              {coverPreview ? (
                <img src={coverPreview} alt="Album cover preview" className="w-full h-full object-cover rounded-lg" />
              ) : (
                <div className="w-full h-full bg-zinc-800 rounded-lg flex items-center justify-center">
                  <Music className="w-12 h-12 text-zinc-600" />
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer rounded-lg">
                <Upload className="w-8 h-8" />
                <input type="file" accept="image/*" onChange={handleCoverSelect} className="hidden" />
              </label>
            </div>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Name
            </label>
            <Input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-zinc-800"
              placeholder="Album Name"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-2">
              Description (optional)
            </label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-zinc-800"
              placeholder="Add an optional description"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()} className="bg-yellow-500 text-white hover:bg-yellow-600">
              Create Album
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
